import { layerLabel } from "../terrain3d/layerSpecs";

/**
 * Index et recherche du catalogue de couches.
 *
 * DEUX PRINCIPES.
 *
 * 1. On indexe des PAIRES (couche, indicateur), pas des couches. Un auteur ne
 *    cherche pas « la couche communes », il cherche « la sécheresse » — qui
 *    est un ensemble d'indicateurs répartis dans cette couche.
 *
 * 2. La recherche TOLÈRE LES FAUTES. Une recherche exacte oblige à connaître
 *    l'orthographe du vocabulaire technique qu'on est justement en train de
 *    découvrir : « evapotranspiration », « qmna », « secheresse » doivent
 *    trouver leur cible, accents et doigts qui glissent compris.
 */

// Casse et accents écartés : personne ne tape « évapotranspiration » avec ses
// accents dans un champ de recherche.
export function normalize(text) {
  return (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

// Exporté : la recherche de mesures (`decisionSearch`) découpe ses documents
// avec le MÊME tokeniseur. Deux découpages différents et « eau_potable »
// devient trouvable ici et pas là.
export function words(text) {
  return normalize(text).split(/[^a-z0-9]+/).filter(Boolean);
}

/**
 * Distance d'édition, plafonnée : au-delà de `max` on arrête, la réponse
 * exacte n'intéresse personne — on veut juste savoir si c'est « proche ».
 * Le plafond évite aussi de comparer intégralement des mots très différents.
 */
export function editDistance(a, b, max = 2) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    let rowBest = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
      if (current[j] < rowBest) rowBest = current[j];
    }
    // Toute la ligne dépasse le plafond : les lignes suivantes ne peuvent que
    // faire pire, inutile de continuer.
    if (rowBest > max) return max + 1;
    previous = current;
  }
  return previous[b.length];
}

// Tolérance proportionnelle à la longueur : sur trois lettres, une faute
// change le mot ; sur dix, c'est une frappe.
function allowedDistance(token) {
  if (token.length <= 3) return 0;
  if (token.length <= 6) return 1;
  return 2;
}

/**
 * Score d'un terme contre une liste de mots. Zéro = ne correspond pas, et
 * l'entrée est écartée : mieux vaut ne rien proposer que du bruit.
 */
export function scoreToken(token, haystackWords) {
  let best = 0;
  const tolerance = allowedDistance(token);
  for (const word of haystackWords) {
    if (word === token) return 100;
    if (word.startsWith(token)) {
      best = Math.max(best, 80);
      continue;
    }
    if (word.includes(token)) {
      best = Math.max(best, 60);
      continue;
    }
    if (tolerance > 0) {
      const distance = editDistance(word, token, tolerance);
      if (distance <= tolerance) best = Math.max(best, 45 - distance * 10);
    }
  }
  return best;
}

/** Index plat : une entrée par couche, une par indicateur. */
export function buildSearchEntries(layerCatalog, availableLayers) {
  const entries = [];
  (layerCatalog || [])
    .filter((layer) => !availableLayers || availableLayers.includes(layer.id))
    .forEach((layer) => {
      entries.push({
        key: `layer:${layer.id}`,
        kind: "layer",
        layerId: layer.id,
        theme: layer.theme,
        label: layerLabel(layer.id),
        detail: layer.summary,
        // Le libellé pèse plus que les tags : une correspondance sur le nom
        // de la chose cherchée doit passer devant une correspondance sur un
        // mot-clé qui l'accompagne.
        primary: words(layerLabel(layer.id)),
        secondary: words([layer.summary, (layer.tags || []).join(" ")].join(" ")),
      });
      (layer.indicators || []).forEach((indicator) => {
        entries.push({
          key: `indicator:${layer.id}:${indicator.id}`,
          kind: "indicator",
          layerId: layer.id,
          indicatorId: indicator.id,
          theme: indicator.theme || layer.theme,
          label: indicator.label,
          group: indicator.group,
          detail: `${layerLabel(layer.id)}${indicator.group ? ` · ${indicator.group}` : ""}`,
          primary: words(indicator.label),
          secondary: words([indicator.group, layerLabel(layer.id), (layer.tags || []).join(" ")].join(" ")),
        });
      });
    });
  return entries;
}

/**
 * Résultats triés par pertinence. Tous les termes doivent correspondre — dans
 * n'importe quel ordre — sans quoi « restriction sécheresse » remonterait tout
 * ce qui parle de l'un OU de l'autre.
 */
export function searchEntries(entries, query, { limit = 20 } = {}) {
  const tokens = normalize(query).split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];

  const scored = [];
  for (const entry of entries) {
    let total = 0;
    let matchesAll = true;
    for (const token of tokens) {
      const primary = scoreToken(token, entry.primary);
      const secondary = scoreToken(token, entry.secondary);
      // Le secondaire vaut moitié moins : il aide à départager, il ne fait
      // pas remonter une entrée à lui seul aussi haut qu'un nom exact.
      const score = Math.max(primary, secondary * 0.5);
      if (score === 0) {
        matchesAll = false;
        break;
      }
      total += score;
    }
    if (matchesAll) scored.push({ entry, score: total });
  }

  scored.sort((a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label, "fr"));
  return scored.slice(0, limit).map((s) => s.entry);
}

/** Entrées rangées par thème, dans l'ordre déclaré par le manifeste. */
export function groupByTheme(entries, themes) {
  return (themes || [])
    .map((theme) => ({
      ...theme,
      entries: entries.filter((entry) => entry.theme === theme.id),
    }))
    .filter((theme) => theme.entries.length);
}
